import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Card,
  Form,
  Input,
  Rate,
  Radio,
  Select,
  Space,
  Typography,
  message,
} from "antd";
import { ArrowLeftOutlined, SaveOutlined } from "@ant-design/icons";

import { myLibraryApi, seasonsApi, worksApi } from "../services/api";
import type { Work } from "../types";
import { usePermissions } from "../hooks/usePermissions";

const { Title } = Typography;

export function MyLibraryEntryFormPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const { canCreate } = usePermissions();
  const isReadOnly = !canCreate;

  const [type, setType] = useState<"book" | "movie" | "series">("book");
  const [search, setSearch] = useState("");

  const { data: worksData, isLoading: worksLoading } = useQuery({
    queryKey: ["works", "my-library-select", type, search],
    queryFn: () =>
      worksApi.list({
        page: 1,
        page_size: 50,
        type,
        search: search || undefined,
        sort: "title",
        order: "asc",
      }),
  });

  const workOptions = useMemo(() => {
    const items = worksData?.items ?? [];
    return items.map((w: Work) => ({ value: w.id, label: w.title }));
  }, [worksData]);

  const selectedWorkId = Form.useWatch("work_id", form) as string | undefined;

  const { data: seasonsData, isLoading: seasonsLoading } = useQuery({
    queryKey: ["series-seasons", selectedWorkId],
    queryFn: () => seasonsApi.list(selectedWorkId as string),
    enabled: type === "series" && !!selectedWorkId,
  });

  const seasonOptions = useMemo(() => {
    const items = seasonsData ?? [];
    return items.map((s) => ({
      value: s.id,
      label: `Sezon ${s.season_number}`,
    }));
  }, [seasonsData]);

  const mutation = useMutation({
    mutationFn: (payload: {
      work_id: string;
      season_id?: string | null;
      finished_at: string;
      rating?: number;
      review?: string;
      private_notes?: string | null;
    }) => myLibraryApi.createEntry(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-library-years"] });
      queryClient.invalidateQueries({ queryKey: ["my-library-stats"] });
      queryClient.invalidateQueries({ queryKey: ["my-library-monthly"] });
      queryClient.invalidateQueries({ queryKey: ["my-library-entries"] });
      message.success("Wpis dodany");
      navigate("/");
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.detail || "Nie udało się dodać wpisu";
      message.error(errorMessage);
    },
  });

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(-1)}
          style={{ padding: 0 }}
        >
          Powrót
        </Button>
        <Title level={2} style={{ marginTop: 8, marginBottom: 0 }}>
          Dodaj wpis
        </Title>
      </div>

      <Card>
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => {
            if (type === "series" && !values.season_id) {
              message.error("Wybierz sezon");
              return;
            }

            mutation.mutate({
              work_id: values.work_id as string,
              season_id:
                type === "series" ? (values.season_id as string) : undefined,
              finished_at: values.finished_at as string,
              rating: values.rating as number | undefined,
              review: values.review as string | undefined,
              private_notes: values.private_notes as string | null | undefined,
            });
          }}
          initialValues={{
            finished_at: new Date().toISOString().slice(0, 10),
          }}
        >
          <Form.Item label="Rodzaj" required>
            <Radio.Group
              optionType="button"
              buttonStyle="solid"
              value={type}
              onChange={(e) => {
                setType(e.target.value as "book" | "movie" | "series");
                form.setFieldValue("work_id", undefined);
                form.setFieldValue("season_id", undefined);
              }}
              options={[
                { label: "Książka", value: "book" },
                { label: "Film", value: "movie" },
                { label: "Serial", value: "series" },
              ]}
            ></Radio.Group>
          </Form.Item>

          <Form.Item
            name="work_id"
            label="Dzieło"
            rules={[{ required: true, message: "Wybierz dzieło" }]}
          >
            <Select
              showSearch
              placeholder="Wybierz dzieło"
              options={workOptions}
              loading={worksLoading}
              filterOption={false}
              onSearch={(v) => setSearch(v)}
              allowClear
            />
          </Form.Item>

          {type === "series" && (
            <Form.Item
              name="season_id"
              label="Sezon"
              rules={[{ required: true, message: "Wybierz sezon" }]}
            >
              <Select
                placeholder={
                  selectedWorkId ? "Wybierz sezon" : "Najpierw wybierz serial"
                }
                options={seasonOptions}
                loading={seasonsLoading}
                disabled={!selectedWorkId}
              />
            </Form.Item>
          )}

          <Form.Item
            name="finished_at"
            label="Data ukończenia"
            rules={[{ required: true, message: "Data jest wymagana" }]}
          >
            <Input type="date" />
          </Form.Item>

          <Form.Item name="rating" label="Ocena">
            <Rate allowClear count={5} />
          </Form.Item>

          <Form.Item name="review" label="Notatka">
            <Input.TextArea rows={4} />
          </Form.Item>

          <Form.Item name="private_notes" label="Prywatne notatki">
            <Input.TextArea rows={4} />
          </Form.Item>

          <Form.Item
            style={{ marginTop: 24, marginBottom: 0, textAlign: "right" }}
          >
            <Space>
              <Button onClick={() => navigate(-1)}>Anuluj</Button>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={mutation.isPending}
                disabled={isReadOnly}
                title={isReadOnly ? "Brak uprawnień do edycji" : undefined}
              >
                Dodaj
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
}

export default MyLibraryEntryFormPage;
